// Material & cost estimate for the house plan (Stage 14d). Turns the wall graph
// + construction spec + openings + footprint into a rough bill of materials with
// editable unit prices. Pure JS, no DOM — unit-tested with Vitest.
//
// HONESTY: this is an "ориентировочный" estimate. Counts are VOLUME-based
// (units = wall volume / unit volume × waste), which is robust when a block's
// own thickness differs from the wall thickness but ignores cutting/coursing.
// Opening volume is subtracted using default opening HEIGHTS (no vertical model
// until Stage 9d). Corner overlap of centreline walls is not deducted. Prices
// are 2026 ballpark defaults the user can override; they vary by region/time.

import { polygonAreaM2 } from "./faces"

// Default opening heights (m) for subtracting opening volume from walls.
export const OPENING_HEIGHT_M = { window: 1.5, door: 2.1 }

// Wall materials. `unitsPerM3` converts net wall volume → primary units (blocks/
// bricks; for timber the unit IS the m³ so unitsPerM3 = 1). `mortarRatio` is m³
// of mortar/glue per m³ of wall (0 for timber). Prices are ₽ per primary unit.
export const WALL_MATERIALS = [
  { id: "aerated", name: "Газобетон D500 600×300×200", unit: "блок", unitsPerM3: 27.8, wasteFactor: 1.05, defaultPrice: 230, mortarRatio: 0.03, mortarName: "Клей для газобетона", mortarUnit: "м³", mortarPrice: 4500 },
  { id: "expanded-clay", name: "Керамзитоблок 390×190×188", unit: "блок", unitsPerM3: 71.4, wasteFactor: 1.05, defaultPrice: 95, mortarRatio: 0.08, mortarName: "Кладочный раствор", mortarUnit: "м³", mortarPrice: 3800 },
  { id: "brick", name: "Кирпич рядовой одинарный", unit: "шт", unitsPerM3: 394, wasteFactor: 1.05, defaultPrice: 18, mortarRatio: 0.23, mortarName: "Кладочный раствор", mortarUnit: "м³", mortarPrice: 3800 },
  { id: "timber", name: "Брус 150×150", unit: "м³", unitsPerM3: 1, wasteFactor: 1.05, defaultPrice: 18000, mortarRatio: 0 },
]

// Strip-foundation concrete (м³). Volume = exterior perimeter × width × depth.
export const FOUNDATION_MATERIALS = [
  { id: "concrete-b22", name: "Бетон B22.5 (М300), лента", unit: "м³", wasteFactor: 1.03, defaultPrice: 5500 },
  { id: "concrete-b15", name: "Бетон B15 (М200), лента", unit: "м³", wasteFactor: 1.03, defaultPrice: 4600 },
]

// Roofing (м²). Area = footprint × pitch factor.
export const ROOF_MATERIALS = [
  { id: "metal-tile", name: "Металлочерепица", unit: "м²", wasteFactor: 1.1, defaultPrice: 650 },
  { id: "corrugated", name: "Профнастил", unit: "м²", wasteFactor: 1.1, defaultPrice: 480 },
  { id: "soft-roof", name: "Мягкая (гибкая) черепица", unit: "м²", wasteFactor: 1.1, defaultPrice: 520 },
]

// Floor/ceiling structure (м²). Area = footprint.
export const FLOOR_MATERIALS = [
  { id: "timber-joist", name: "Деревянное перекрытие (балки + настил)", unit: "м²", wasteFactor: 1.05, defaultPrice: 1900 },
  { id: "rc-slab", name: "Ж/б плиты перекрытия", unit: "м²", wasteFactor: 1.02, defaultPrice: 3500 },
]

export const CATALOGUES = {
  wall: WALL_MATERIALS,
  foundation: FOUNDATION_MATERIALS,
  roof: ROOF_MATERIALS,
  floor: FLOOR_MATERIALS,
}

export const DEFAULT_SELECTIONS = { wall: "aerated", foundation: "concrete-b22", roof: "metal-tile", floor: "timber-joist" }
export const DEFAULT_PARAMS = { foundationWidthM: 0.4, foundationDepthM: 0.6, roofPitchFactor: 1.2 }

function materialById(category, id) {
  return (CATALOGUES[category] ?? []).find((m) => m.id === id) ?? CATALOGUES[category]?.[0] ?? null
}

// Wall lengths (m) by kind from the graph.
function wallLengthsByKind(graph) {
  const byId = new Map((graph?.vertices ?? []).map((v) => [v.id, v]))
  let exterior = 0, interior = 0
  for (const w of graph?.walls ?? []) {
    const a = byId.get(w.a), b = byId.get(w.b)
    if (!a || !b) continue
    const len = Math.hypot(b.xM - a.xM, b.yM - a.yM)
    if (w.kind === "interior") interior += len
    else exterior += len
  }
  return { exterior, interior }
}

// Opening cross-section area (width × default height) summed by the kind of the
// wall each opening sits on, so it can be deducted from that wall's volume.
function openingAreasByWallKind(graph, openings) {
  const wallKind = new Map((graph?.walls ?? []).map((w) => [w.id, w.kind ?? "exterior"]))
  let exterior = 0, interior = 0
  for (const o of openings ?? []) {
    const h = OPENING_HEIGHT_M[o.kind] ?? OPENING_HEIGHT_M.window
    const area = (o.widthM ?? 0) * h
    if (wallKind.get(o.wallId) === "interior") interior += area
    else exterior += area
  }
  return { exterior, interior }
}

const round1 = (n) => Math.round(n * 10) / 10

// Compute the bill of materials. Returns { lines, total, geometry } where each
// line is { category, key, name, quantity, unit, unitPrice, cost }. `prices`
// overrides a line's unit price by its key; `selections`/`params` choose
// materials and foundation/roof dimensions. Missing inputs fall back to defaults.
export function computeEstimate({ graph, footprint, openings = [], construction = {}, selections = {}, prices = {}, params = {} } = {}) {
  const sel = { ...DEFAULT_SELECTIONS, ...selections }
  const p = { ...DEFAULT_PARAMS, ...params }
  const H = construction.wallHeightM ?? 3
  const tExt = construction.exteriorThicknessM ?? 0.4
  const tInt = construction.interiorThicknessM ?? 0.12

  const len = wallLengthsByKind(graph)
  const openArea = openingAreasByWallKind(graph, openings)
  const footprintArea = Array.isArray(footprint) && footprint.length >= 3 ? polygonAreaM2(footprint) : 0
  const perimeter = len.exterior // exterior walls ≈ the outer perimeter

  // Net wall volume (gross − openings), clamped at 0. openArea is the opening
  // cross-section (width × default height, m²); × thickness gives the m³ removed.
  const vExt = Math.max(0, len.exterior * tExt * H - openArea.exterior * tExt)
  const vInt = Math.max(0, len.interior * tInt * H - openArea.interior * tInt)
  const wallVolume = vExt + vInt

  const lines = []
  const priceOf = (key, fallback) => (Number.isFinite(prices[key]) ? prices[key] : fallback)

  // --- walls ---
  const wm = materialById("wall", sel.wall)
  if (wm && wallVolume > 0) {
    const units = wm.unit === "м³"
      ? round1(wallVolume * wm.wasteFactor)
      : Math.ceil(wallVolume * wm.wasteFactor * wm.unitsPerM3)
    const price = priceOf("wall", wm.defaultPrice)
    lines.push({ category: "wall", key: "wall", name: wm.name, quantity: units, unit: wm.unit, unitPrice: price, cost: Math.round(units * price) })
    if (wm.mortarRatio > 0) {
      const mortar = round1(wallVolume * wm.mortarRatio)
      const mPrice = priceOf("wall-mortar", wm.mortarPrice)
      lines.push({ category: "wall", key: "wall-mortar", name: wm.mortarName, quantity: mortar, unit: wm.mortarUnit, unitPrice: mPrice, cost: Math.round(mortar * mPrice) })
    }
  }

  // --- foundation (strip) ---
  const fm = materialById("foundation", sel.foundation)
  if (fm && perimeter > 0) {
    const vol = round1(perimeter * p.foundationWidthM * p.foundationDepthM * fm.wasteFactor)
    const price = priceOf("foundation", fm.defaultPrice)
    lines.push({ category: "foundation", key: "foundation", name: fm.name, quantity: vol, unit: fm.unit, unitPrice: price, cost: Math.round(vol * price) })
  }

  // --- roof ---
  const rm = materialById("roof", sel.roof)
  if (rm && footprintArea > 0) {
    const area = round1(footprintArea * p.roofPitchFactor * rm.wasteFactor)
    const price = priceOf("roof", rm.defaultPrice)
    lines.push({ category: "roof", key: "roof", name: rm.name, quantity: area, unit: rm.unit, unitPrice: price, cost: Math.round(area * price) })
  }

  // --- floor / ceiling ---
  const flm = materialById("floor", sel.floor)
  if (flm && footprintArea > 0) {
    const area = round1(footprintArea * flm.wasteFactor)
    const price = priceOf("floor", flm.defaultPrice)
    lines.push({ category: "floor", key: "floor", name: flm.name, quantity: area, unit: flm.unit, unitPrice: price, cost: Math.round(area * price) })
  }

  const total = lines.reduce((s, l) => s + l.cost, 0)
  return { lines, total, geometry: { wallVolume: round1(wallVolume), footprintArea: round1(footprintArea), perimeter: round1(perimeter) } }
}
